import React from "react";
import { useNormalization } from "../../NormalizationContext";

const COLORS = {
    income: "#8884d8",
    expense: "#5cd6d6",
    balance: "#4d79ff",
    white: "#fff",
    darkText: "black",
};

export default function SummaryBar({ income, expense, balance, originalIncome, originalExpense, originalBalance, isMobile = false }) {
    const { hasNormalization, resetAll } = useNormalization();

    if (isMobile) {
        return <MobileSummary
            income={income}
            expense={expense}
            balance={balance}
            originalIncome={originalIncome}
            originalExpense={originalExpense}
            originalBalance={originalBalance}
            hasNormalization={hasNormalization}
            resetAll={resetAll}
        />;
    }

    const cards = [
        { label: "הכנסות", amount: income, original: originalIncome, bg: COLORS.income },
        { label: "הוצאות", amount: expense, original: originalExpense, bg: COLORS.expense },
        { label: "מאזן", amount: balance, original: originalBalance, bg: balance >= 0 ? COLORS.balance : "#e74c3c" },
    ];

    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "28px", marginTop: "20px" }}>
            <div style={{ display: "flex", justifyContent: "center" }}>
                {cards.map((card) => (
                    <div key={card.label} style={{ ...desktopCardStyle, background: card.bg }}>
                        <div style={desktopLabelStyle}>{card.label}</div>
                        <div style={desktopAmountStyle}>{formatAmount(card.amount)}₪</div>
                        {hasNormalization && card.amount !== card.original && (
                            <div style={desktopOriginalStyle}>{formatAmount(card.original)}₪</div>
                        )}
                    </div>
                ))}
            </div>
            {hasNormalization && (
                <button onClick={resetAll} style={resetBtnStyle}>
                    איפוס התאמות
                </button>
            )}
        </div>
    );
}

function MobileSummary({ income, expense, balance, originalIncome, originalExpense, originalBalance, hasNormalization, resetAll }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "16px", marginTop: "10px" }}>
            <div style={{
                width: "92%",
                background: "linear-gradient(145deg, #2EB5A0 0%, #1E8A9A 50%, #1B6B8A 100%)",
                borderRadius: 22,
                boxShadow: "0 8px 24px rgba(30, 138, 154, 0.3)",
                padding: "22px 16px 18px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}>
                <div style={{
                    fontSize: 13,
                    fontWeight: "500",
                    color: "rgba(255,255,255,0.7)",
                    letterSpacing: "0.5px",
                    marginBottom: 6,
                }}>
                    מאזן חודשי
                </div>
                <div style={{
                    fontSize: 34,
                    fontWeight: "700",
                    color: "#fff",
                    direction: "ltr",
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-1px",
                    lineHeight: 1.1,
                }}>
                    {balance < 0 ? "-" : ""}{formatAmount(Math.abs(balance))}₪
                </div>
                {hasNormalization && balance !== originalBalance && (
                    <div style={{
                        fontSize: 12,
                        color: "rgba(255,255,255,0.45)",
                        textDecoration: "line-through",
                        marginTop: 3,
                    }}>
                        {formatAmount(originalBalance)}₪
                    </div>
                )}

                <div style={{
                    display: "flex",
                    gap: 10,
                    width: "100%",
                    marginTop: 16,
                }}>
                    <div style={mobilePillStyle(false)}>
                        <span style={mobilePillLabelStyle}>הכנסות</span>
                        <span style={mobilePillAmountStyle}>{formatAmount(income)}₪</span>
                        {hasNormalization && income !== originalIncome && (
                            <span style={mobilePillOriginalStyle}>{formatAmount(originalIncome)}₪</span>
                        )}
                    </div>
                    <div style={mobilePillStyle(true)}>
                        <span style={mobilePillLabelStyle}>הוצאות</span>
                        <span style={mobilePillAmountStyle}>{formatAmount(expense)}₪</span>
                        {hasNormalization && expense !== originalExpense && (
                            <span style={mobilePillOriginalStyle}>{formatAmount(originalExpense)}₪</span>
                        )}
                    </div>
                </div>
            </div>

            {hasNormalization && (
                <button onClick={resetAll} style={resetBtnStyle}>
                    איפוס התאמות
                </button>
            )}
        </div>
    );
}

const mobilePillStyle = (isExpense) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 8px",
    borderRadius: 14,
    background: isExpense
        ? "rgba(255, 100, 100, 0.75)"
        : "rgba(255, 255, 255, 0.18)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
});

const mobilePillLabelStyle = {
    fontSize: 13,
    fontWeight: "600",
    color: "#fff",
    marginBottom: 1,
};

const mobilePillAmountStyle = {
    fontSize: 17,
    fontWeight: "700",
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
};

const mobilePillOriginalStyle = {
    fontSize: 10,
    color: "rgba(255,255,255,0.45)",
    textDecoration: "line-through",
    marginTop: 1,
};

const desktopCardStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "160px",
    height: "75px",
    borderRadius: "20px",
    margin: "0 25px",
};

const desktopLabelStyle = {
    fontSize: "1.25rem",
    fontWeight: "bold",
    color: COLORS.darkText,
    marginBottom: "2px",
};

const desktopAmountStyle = {
    fontSize: "1.25rem",
    fontWeight: "normal",
    color: COLORS.white,
    marginTop: 0,
};

const desktopOriginalStyle = {
    fontSize: "11px",
    color: "rgba(255,255,255,0.7)",
    marginTop: "1px",
    textDecoration: "line-through",
};

const resetBtnStyle = {
    marginTop: "10px",
    background: "none",
    border: "1px solid #ccc",
    borderRadius: "14px",
    padding: "3px 14px",
    fontSize: "12px",
    color: "#888",
    cursor: "pointer",
    transition: "all 0.2s ease",
};

function formatAmount(amount) {
    return Number(Math.abs(amount)).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
